/**
 * LoaderScreen — первый экран перед любым другим UI Luna Protokol.
 *
 * Показывается каждый раз при открытии приложения, до AccessGate /
 * OnboardingShell / OfertaShell / основного интерфейса. Пользователь
 * видит его даже если доступ не одобрен.
 *
 * Поведение:
 *   1. При маунте → выбираем случайный концепт-арт + случайный звук
 *   2. Длительность: pickDuration(5000, 8000) — случайная 5–8 сек
 *   3. Haptic light при старте (Telegram)
 *   4. Звук: HTMLAudioElement, громкость 0.6
 *   5. Entry: картинка fade-in 900мс (scale 1.05→1, blur 8→0)
 *   6. Exit — DIP TO BLACK в 3 фазы:
 *        a) Image fade-out   (600мс) — картинка плавно гаснет до
 *           чёрного фона лоадера (#050612)
 *        b) Black hold       (350мс) — пауза на чёрном экране
 *        c) Overlay fade-out (750мс) — весь лоадер прозрачнеет,
 *           открывая основной UI (который параллельно fade-in'ится)
 *   7. В фазе (a) диспатчим 'luna:loader-black' чтобы App-обёртка
 *      в main.tsx начала свой fade-in синхронно под чёрным экраном.
 *      Когда overlay фадится, App уже полностью виден.
 *
 * z-index 9900 — топ всего, даже выше OfertaShell (9100).
 */
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  pickRandomImage,
  pickRandomSound,
  pickDuration,
} from './assets';
import { getUserSettings } from '@/hooks/useUserSettings';

interface LoaderScreenProps {
  onDone: () => void;
}

type ExitPhase = 'none' | 'image-fading' | 'black-hold' | 'overlay-fading';

export function LoaderScreen({ onDone }: LoaderScreenProps) {
  const [image] = useState(pickRandomImage);
  const [sound] = useState(pickRandomSound);
  const [duration] = useState(() => pickDuration(5000, 8000));
  const [progress, setProgress] = useState(0);
  const [exitPhase, setExitPhase] = useState<ExitPhase>('none');
  const mountedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    const haptic = () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tg = (window as any).Telegram?.WebApp?.HapticFeedback;
        tg?.impactOccurred?.('light');
      } catch {
        /* silent */
      }
    };

    // Haptic «light» сразу на маунте
    haptic();

    // 3–5 рандомных haptic-тиков во время показа, синхронно с
    // «рывками» прогресс-полосок. Распределены равномерно с jitter'ом
    // в зоне 20–85% длительности (чтобы не налезали на entry/exit).
    const tickCount = 3 + Math.floor(Math.random() * 3);
    const tickTimes: number[] = [];
    for (let i = 0; i < tickCount; i++) {
      const base = 0.2 + (0.65 * i) / (tickCount - 1 || 1);
      const jitter = (Math.random() - 0.5) * 0.08;
      tickTimes.push(Math.max(0.18, Math.min(0.88, base + jitter)));
    }
    const tickTimeouts = tickTimes.map((t) =>
      window.setTimeout(haptic, t * duration),
    );

    // Звук — учитываем настройку пользователя
    const audio = new Audio(sound);
    audio.volume = 0.6;
    audio.preload = 'auto';
    if (getUserSettings().loaderSoundEnabled) {
      void audio.play().catch(() => { /* autoplay may be blocked */ });
    }

    // Прогресс-линия: обновляем каждые 50 мс
    const ticker = window.setInterval(() => {
      const elapsed = Date.now() - mountedAtRef.current;
      setProgress(Math.min(100, (elapsed / duration) * 100));
    }, 50);

    // Таймлайн выхода: image-fading → black-hold → overlay-fading → onDone
    const t1 = window.setTimeout(() => {
      setExitPhase('image-fading');
      // Звук — fade-out синхронно с картинкой (600мс)
      fadeOutAudio(audio, 600);
    }, duration);

    const t2 = window.setTimeout(() => {
      setExitPhase('black-hold');
      // Чёрный экран активен → оповещаем Root что App можно
      // проявлять под чёрным слоем (crossfade под dip-to-black)
      window.dispatchEvent(new CustomEvent('luna:loader-black'));
    }, duration + 600);

    const t3 = window.setTimeout(() => {
      setExitPhase('overlay-fading');
    }, duration + 600 + 350);

    const tDone = window.setTimeout(
      () => onDone(),
      duration + 600 + 350 + 750,
    );

    return () => {
      window.clearInterval(ticker);
      [t1, t2, t3, tDone].forEach((t) => window.clearTimeout(t));
      tickTimeouts.forEach((t) => window.clearTimeout(t));
      try {
        audio.pause();
      } catch {
        /* silent */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imageOpacity = exitPhase === 'none' ? 1 : 0;
  const overlayGone = exitPhase === 'overlay-fading';

  return (
    <AnimatePresence>
      {!overlayGone && (
        <motion.div
          key="loader-overlay"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.75, ease: [0.32, 0.72, 0, 1] }}
          className="fixed inset-0 z-[9900] overflow-hidden"
          style={{
            background: '#000000',
            touchAction: 'none',
          }}
        >
          {/* Radial bloom — золотая вспышка из центра при старте:
              разрастается и гаснет за 1.4с, создаёт «рождение света». */}
          {exitPhase === 'none' && (
            <motion.div
              className="pointer-events-none absolute inset-0"
              style={{
                background:
                  'radial-gradient(circle at 50% 50%, rgba(245,217,126,0.32) 0%, rgba(245,217,126,0.12) 18%, transparent 55%)',
                mixBlendMode: 'screen',
              }}
              initial={{ opacity: 0, scale: 0.2 }}
              animate={{ opacity: [0, 1, 0], scale: [0.2, 1.6, 2.6] }}
              transition={{
                duration: 1.4,
                ease: 'easeOut',
                times: [0, 0.45, 1],
              }}
            />
          )}

          {/* Image — 1080x1920. Entry: dolly-in zoom 1.22→1.0,
              blur 14→0, opacity 0→1, плюс subtle drift y 24→0.
              Exit: scale 1.0→1.02, blur 0→6, opacity 1→0. */}
          <motion.div
            initial={{
              opacity: 0,
              scale: 1.22,
              filter: 'blur(14px)',
              y: 24,
            }}
            animate={{
              opacity: imageOpacity,
              scale: exitPhase === 'none' ? 1 : 1.02,
              filter: exitPhase === 'none' ? 'blur(0px)' : 'blur(6px)',
              y: 0,
            }}
            transition={{
              opacity: {
                duration: exitPhase === 'none' ? 0.75 : 0.6,
                delay: exitPhase === 'none' ? 0.15 : 0,
                ease: [0.32, 0.72, 0, 1],
              },
              scale: {
                duration: exitPhase === 'none' ? 1.35 : 0.6,
                ease: [0.22, 0.68, 0.16, 1],
              },
              filter: {
                duration: exitPhase === 'none' ? 1.05 : 0.6,
                ease: 'easeOut',
              },
              y: {
                duration: 1.2,
                ease: [0.22, 0.68, 0.16, 1],
              },
            }}
            className="absolute inset-0"
          >
            <img
              src={image}
              alt=""
              draggable={false}
              className="h-full w-full select-none"
              style={{
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          </motion.div>

          {/* Iris curtains — чёрные шторки сверху/снизу убегают к краям,
              открывая кадр как в кино. Только entry, на exit их нет. */}
          {exitPhase === 'none' && (
            <>
              <motion.div
                className="pointer-events-none absolute inset-x-0 top-0"
                style={{ background: '#000000' }}
                initial={{ height: '28%' }}
                animate={{ height: '0%' }}
                transition={{
                  duration: 1.1,
                  delay: 0.2,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
              <motion.div
                className="pointer-events-none absolute inset-x-0 bottom-0"
                style={{ background: '#000000' }}
                initial={{ height: '28%' }}
                animate={{ height: '0%' }}
                transition={{
                  duration: 1.1,
                  delay: 0.2,
                  ease: [0.22, 1, 0.36, 1],
                }}
              />
            </>
          )}

          {/* Vignette */}
          <motion.div
            animate={{ opacity: imageOpacity }}
            transition={{ duration: 0.6 }}
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(5,6,18,0.35) 0%, transparent 18%, transparent 82%, rgba(5,6,18,0.85) 100%)',
            }}
          />

          {/* Shimmer sweep — останавливается когда начинается выход */}
          {exitPhase === 'none' && (
            <motion.div
              className="pointer-events-none absolute inset-x-0 top-0 h-[40%]"
              style={{
                background:
                  'linear-gradient(180deg, transparent 0%, rgba(245,217,126,0.08) 50%, transparent 100%)',
                mixBlendMode: 'screen',
                filter: 'blur(20px)',
              }}
              initial={{ y: '-120%' }}
              animate={{ y: '320%' }}
              transition={{
                duration: 2.4,
                ease: 'easeInOut',
                repeat: Infinity,
                repeatDelay: 0.4,
              }}
            />
          )}

          {/* Noise */}
          <motion.div
            animate={{ opacity: imageOpacity * 0.035 }}
            transition={{ duration: 0.6 }}
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml;utf8,${NOISE_SVG}")`,
              backgroundSize: '160px 160px',
              mixBlendMode: 'soft-light',
            }}
          />

          {/* Top progress line — заполняется справа-налево (зеркально).
              В safe-area-inset-top, всегда далеко от центра картинки. */}
          <motion.div
            animate={{ opacity: imageOpacity }}
            transition={{ duration: 0.5 }}
            className="absolute inset-x-0 top-0"
            style={{ paddingTop: 'env(safe-area-inset-top)' }}
          >
            <div
              className="relative h-[2px] w-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <motion.div
                className="absolute right-0 top-0 h-full"
                style={{
                  width: `${progress}%`,
                  background:
                    'linear-gradient(90deg, rgba(245,217,126,0.2) 0%, rgba(245,217,126,1) 50%, rgba(245,217,126,0.2) 100%)',
                  boxShadow: '0 0 12px rgba(245,217,126,0.7)',
                  transition: 'width 80ms linear',
                }}
              />
            </div>
          </motion.div>

          {/* Bottom progress line — заполняется слева-направо.
              Синхронна с top, обе гаснут вместе с картинкой. */}
          <motion.div
            animate={{ opacity: imageOpacity }}
            transition={{ duration: 0.5 }}
            className="absolute inset-x-0 bottom-0"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div
              className="relative h-[2px] w-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <motion.div
                className="absolute left-0 top-0 h-full"
                style={{
                  width: `${progress}%`,
                  background:
                    'linear-gradient(90deg, rgba(245,217,126,0.2) 0%, rgba(245,217,126,1) 50%, rgba(245,217,126,0.2) 100%)',
                  boxShadow: '0 0 12px rgba(245,217,126,0.7)',
                  transition: 'width 80ms linear',
                }}
              />
            </div>
          </motion.div>

          {/* Brand micro-label */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: imageOpacity === 1 ? 1 : 0 }}
            transition={{
              delay: imageOpacity === 1 ? 0.8 : 0,
              duration: 0.6,
            }}
            className="pointer-events-none absolute inset-x-0 bottom-5 flex justify-center"
            style={{
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            <span
              className="font-display text-[10px] uppercase tracking-[0.38em]"
              style={{ color: 'rgba(255,255,255,0.32)' }}
            >
              LUN∆ PRO₮OKOL
            </span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ──────────────────────────────────────────────────────────────────────

const NOISE_SVG = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'>
    <filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.1' numOctaves='2' stitchTiles='stitch'/></filter>
    <rect width='100%' height='100%' filter='url(#n)' opacity='1'/>
  </svg>`,
);

function fadeOutAudio(audio: HTMLAudioElement, durationMs: number): void {
  const startVolume = audio.volume;
  const startTime = Date.now();
  const step = () => {
    const elapsed = Date.now() - startTime;
    if (elapsed >= durationMs) {
      audio.volume = 0;
      try {
        audio.pause();
      } catch {
        /* silent */
      }
      return;
    }
    const t = elapsed / durationMs;
    audio.volume = startVolume * (1 - t);
    requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
